import Button from '@components/buttons/button/button.component'
import Callout from '@components/callout/callout.component'
import Modal from '@components/modals/modal/modal.component'
import ToastHelper from '@helpers/toast.helper'
import LogService from '@services/log/log.service'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FinancialOperationService } from '@/hub-shared'
import './modal-unblock-payment.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  openModal: boolean
  transactionId: string
  value: string
  date: string
  paymentType: string
  closeModal?: () => any
}

export default ({ openModal, transactionId, value, date, paymentType, closeModal }: Props) => {
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const financialService = useMemo(() => new FinancialOperationService(), [])
  const [t] = useTranslation('modalUnblockPayment')
  const [loading, setLoading] = useState(false)

  logService.init('Modal Unblock Payment')

  const handleCloseModal = useCallback(() => {
    if (closeModal && typeof closeModal === 'function') {
      closeModal()
    }
  }, [closeModal])

  const postUnblockPayment = useCallback(() => {
    setLoading(true)
    financialService
      .postUnblockPayment(transactionId)
      .then((e) => {
        if (e?.[0]) {
          toast.success(t('success'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
          if (closeModal && typeof closeModal === 'function') {
            closeModal()
          }
        }
      })
      .catch((error) => {
        logService.error({ message: 'Failed to unblock payment', error })
        toast.error(t('fail'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [transactionId, closeModal, logService, t, financialService, toast])

  return (
    <Modal
      className="modal-unblock-payment"
      open={openModal}
      onCloseChange={() => handleCloseModal()}
      header={<h5 className="m-0">{t('unblock')}</h5>}
      footer={
        <div className="display-flex justify-content-space-between" style={{ width: '100%' }}>
          <Button
            onClick={() => handleCloseModal()}
            disabled={loading}
            color="transparent"
            textColor="var(--text_color)"
            borderColor="var(--text_color)"
          >
            {t('cancel')}
          </Button>
          <Button onClick={() => postUnblockPayment()} loading={loading}>
            {t('confirm')}
          </Button>
        </div>
      }
    >
      <div className="content">
        <span
          dangerouslySetInnerHTML={{
            __html: t('message', {
              value: value,
              date: date,
            }),
          }}
        />
        <Callout title={t('callout.title')} icon={'fa-solid fa-diamond-exclamation'}>
          <span>
            <strong>{t('callout.reason.text')}</strong> {t(`callout.reason.${paymentType}`)}
          </span>
        </Callout>
      </div>
    </Modal>
  )
}
